/**
 * CSS-drawn document thumbnails for the HERO "Dokumente" module. Three variants
 * (page / spreadsheet / image), each absolutely positioned inside the 146px
 * card preview area. Pure markup — no raster assets. 1:1 with prototype L680.
 */
import { Image as ImageIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

/** Shared white "sheet" that the page + table skeletons sit on. */
const sheet: CSSProperties = {
  position: "absolute",
  top: 11,
  left: 13,
  right: 13,
  height: 230,
  background: "#fff",
  border: "1px solid #E6E8EB",
  borderRadius: "3px 3px 0 0",
  boxShadow: "0 3px 10px -6px rgba(11,15,25,0.2)",
  padding: 10,
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

/** Page-skeleton thumbnail (text document). */
export function ThumbText(): ReactNode {
  const line = (w: string): CSSProperties => ({
    width: w,
    height: 2.5,
    borderRadius: 2,
    background: "#E9EBEE",
  });
  const accent = (w: string): CSSProperties => ({
    width: w,
    height: 2.5,
    borderRadius: 2,
    background: "#D7DBE0",
  });
  return (
    <div style={sheet}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 2,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <div
            style={{
              width: 24,
              height: 8,
              borderRadius: 2,
              background: "#CBD0D7",
            }}
          />
          <div
            style={{
              width: 40,
              height: 2.5,
              borderRadius: 2,
              background: "#E6E9ED",
            }}
          />
          <div
            style={{
              width: 32,
              height: 2.5,
              borderRadius: 2,
              background: "#E6E9ED",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            alignItems: "flex-end",
          }}
        >
          <div
            style={{
              width: 28,
              height: 2.5,
              borderRadius: 2,
              background: "#E6E9ED",
            }}
          />
          <div
            style={{
              width: 20,
              height: 2.5,
              borderRadius: 2,
              background: "#E6E9ED",
            }}
          />
        </div>
      </div>
      <div
        style={{
          width: "62%",
          height: 5,
          borderRadius: 2,
          background: "#C3C8CF",
          marginTop: 5,
        }}
      />
      <div style={{ ...line("100%"), marginTop: 3 }} />
      <div style={line("95%")} />
      <div style={line("98%")} />
      <div style={line("90%")} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 6,
        }}
      >
        <div style={line("38%")} />
        <div style={accent("20%")} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={line("44%")} />
        <div style={accent("18%")} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div style={line("40%")} />
        <div style={accent("22%")} />
      </div>
    </div>
  );
}

/** Spreadsheet-skeleton thumbnail (table document). */
export function ThumbTable(): ReactNode {
  const headRow = (
    <div style={{ display: "flex", borderBottom: "1px solid #E3E6EA" }}>
      <div
        style={{
          flex: 2,
          height: 9,
          background: "#E0E4E9",
          borderRight: "1px solid #fff",
        }}
      />
      <div
        style={{
          flex: 1,
          height: 9,
          background: "#E0E4E9",
          borderRight: "1px solid #fff",
        }}
      />
      <div
        style={{
          flex: 1,
          height: 9,
          background: "#E0E4E9",
          borderRight: "1px solid #fff",
        }}
      />
      <div style={{ flex: 1, height: 9, background: "#E0E4E9" }} />
    </div>
  );
  const bodyRow = (last: boolean): ReactNode => (
    <div
      style={{
        display: "flex",
        borderBottom: last ? "none" : "1px solid #EEF0F2",
      }}
    >
      <div style={{ flex: 2, height: 8, borderRight: "1px solid #F2F4F6" }} />
      <div style={{ flex: 1, height: 8, borderRight: "1px solid #F2F4F6" }} />
      <div style={{ flex: 1, height: 8, borderRight: "1px solid #F2F4F6" }} />
      <div style={{ flex: 1, height: 8 }} />
    </div>
  );
  return (
    <div style={{ ...sheet, gap: 0 }}>
      <div
        style={{
          width: "50%",
          height: 5,
          borderRadius: 2,
          background: "#C3C8CF",
          marginBottom: 7,
        }}
      />
      {headRow}
      {bodyRow(false)}
      {bodyRow(false)}
      {bodyRow(false)}
      {bodyRow(false)}
      {bodyRow(false)}
      {bodyRow(true)}
    </div>
  );
}

/** Image-tile thumbnail (photo document). */
export function ThumbImage(): ReactNode {
  return (
    <div
      style={{
        position: "absolute",
        top: 11,
        left: 13,
        right: 13,
        bottom: 0,
        borderRadius: "3px 3px 0 0",
        boxShadow: "0 3px 10px -6px rgba(11,15,25,0.2)",
        background:
          "linear-gradient(150deg, #C9D6CE 0%, #E4DCC9 52%, #D3C7BE 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span style={{ color: "rgba(255,255,255,0.85)", display: "inline-flex" }}>
        <ImageIcon width={28} height={28} strokeWidth={1.6} />
      </span>
    </div>
  );
}
